import { forwardRef, useMemo } from 'react'
import '../Financeiro/AccountsReceivablePdfCanvas.css'
import { formatDateOnly } from '../../../utils/dateUtils'

const COMPANY_NAME = 'Pró Médico Ltda'
const COMPANY_FOOTER = 'Pró Médico Ltda — Material Médico-Hospitalar | Pelotas/RS'

export const VENDAS_STATUS_LABELS = {
  draft: 'Rascunho',
  confirmed: 'Confirmada',
  reservation: 'Reservada',
  waiting: 'Aguardando',
  completed: 'Concluída',
  cancelled: 'Cancelada',
}

export const VENDAS_COLUMN_DEFS = [
  { id: 'number', label: 'Número', align: 'left', defaultOn: true },
  { id: 'date', label: 'Data', align: 'left', defaultOn: true },
  { id: 'customer', label: 'Cliente', align: 'left', defaultOn: true },
  { id: 'seller', label: 'Vendedor', align: 'left', defaultOn: true },
  { id: 'status', label: 'Status', align: 'center', defaultOn: true },
  { id: 'document', label: 'Documento', align: 'center', defaultOn: false },
  { id: 'payment', label: 'Pagamento', align: 'left', defaultOn: true },
  { id: 'total', label: 'Total', align: 'right', defaultOn: true },
]

export const DEFAULT_VENDAS_COLUMNS = VENDAS_COLUMN_DEFS
  .filter((c) => c.defaultOn)
  .map((c) => c.id)

function formatCurrency(value) {
  const num = Number(value) || 0
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(num)
}

function formatDate(value) {
  return formatDateOnly(value) || '-'
}

function statusLabel(status) {
  return VENDAS_STATUS_LABELS[status] || status || '-'
}

function documentLabel(row) {
  const type = row.fiscal_document_type
  if (!type) return '-'
  const map = { cupom: 'Cupom', nfce: 'NFC-e', nfe: 'NF-e' }
  return map[String(type).toLowerCase()] || String(type)
}

function buildFilterSummary(filters) {
  const parts = []

  if (filters?.dateFrom && filters?.dateTo) {
    parts.push(`Período: ${formatDate(filters.dateFrom)} a ${formatDate(filters.dateTo)}`)
  } else if (filters?.dateFrom) {
    parts.push(`A partir de ${formatDate(filters.dateFrom)}`)
  } else if (filters?.dateTo) {
    parts.push(`Até ${formatDate(filters.dateTo)}`)
  }

  if (Array.isArray(filters?.status) && filters.status.length) {
    parts.push(
      `Status: ${filters.status.map((s) => VENDAS_STATUS_LABELS[s] || s).join(', ')}`
    )
  }

  const customers = Array.isArray(filters?.selectedCustomerNames)
    ? filters.selectedCustomerNames.filter(Boolean)
    : []
  if (customers.length === 1) parts.push(`Cliente: ${customers[0]}`)
  else if (customers.length > 1) parts.push(`${customers.length} clientes`)
  else parts.push('Todos os clientes')

  const sellers = Array.isArray(filters?.selectedSellerNames)
    ? filters.selectedSellerNames.filter(Boolean)
    : []
  if (sellers.length === 1) parts.push(`Vendedor: ${sellers[0]}`)
  else if (sellers.length > 1) parts.push(`${sellers.length} vendedores`)
  else parts.push('Todos os vendedores')

  if (filters?.search?.trim()) {
    parts.push(`Busca: "${filters.search.trim()}"`)
  }

  return parts.join(' · ')
}

function cellAlignClass(align) {
  if (align === 'right') return 'right'
  if (align === 'center') return 'center'
  return undefined
}

function renderCell(columnId, row) {
  switch (columnId) {
    case 'number':
      return row.number || `#${row.id}`
    case 'date':
      return formatDate(row.sale_date)
    case 'customer':
      return (
        <>
          <p className="ar-pdf-desc-main">{row.customer_name || 'Consumidor'}</p>
          {row.customer_cpf_cnpj && (
            <p className="ar-pdf-desc-sub">{row.customer_cpf_cnpj}</p>
          )}
        </>
      )
    case 'seller':
      return row.seller_name || '-'
    case 'status':
      return (
        <span className={`ar-pdf-status ${row.status === 'cancelled' ? 'cancelled' : row.status === 'completed' ? 'paid' : 'upcoming'}`}>
          {statusLabel(row.status)}
        </span>
      )
    case 'document':
      return documentLabel(row)
    case 'payment':
      return row.payment_methods || '-'
    case 'total':
      return formatCurrency(row.final_amount ?? row.total_amount)
    default:
      return '-'
  }
}

const VendasReportPdfCanvas = forwardRef(function VendasReportPdfCanvas(
  {
    sales = [],
    filters = {},
    columns = DEFAULT_VENDAS_COLUMNS,
    chunkMeta = null,
  },
  ref
) {
  const visibleColumns = useMemo(() => {
    const selected = new Set(columns)
    const ordered = VENDAS_COLUMN_DEFS.filter((c) => selected.has(c.id))
    return ordered.length ? ordered : VENDAS_COLUMN_DEFS.filter((c) => c.id === 'number')
  }, [columns])

  const totalsSource = chunkMeta?.totalsSales || sales
  const isFirstChunk = !chunkMeta || chunkMeta.index === 0
  const isLastChunk = !chunkMeta || chunkMeta.index === chunkMeta.total - 1

  const totals = useMemo(() => {
    let amount = 0
    let cancelledAmount = 0
    let cancelledCount = 0

    totalsSource.forEach((row) => {
      const value = Number(row.final_amount ?? row.total_amount) || 0
      if (row.status === 'cancelled') {
        cancelledAmount += value
        cancelledCount += 1
      } else {
        amount += value
      }
    })

    return {
      count: totalsSource.length,
      amount,
      cancelledAmount,
      cancelledCount,
    }
  }, [totalsSource])

  const issuedAt = new Date().toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })

  return (
    <div ref={ref} className="ar-pdf-canvas">
      <header className="ar-pdf-header">
        <div className="ar-pdf-brand">
          <div className="ar-pdf-logo">PM</div>
          <div>
            <p className="ar-pdf-company-name">{COMPANY_NAME}</p>
            <p className="ar-pdf-company-sub">Relatório de vendas</p>
          </div>
        </div>
        <div className="ar-pdf-title-block">
          <h1 className="ar-pdf-title">
            {isFirstChunk ? 'Relação de Vendas' : 'Relação de Vendas (cont.)'}
          </h1>
          <p className="ar-pdf-meta">Emissão: {issuedAt}</p>
          {isFirstChunk ? (
            <p className="ar-pdf-meta">{totals.count} venda(s)</p>
          ) : (
            <p className="ar-pdf-meta">
              Parte {chunkMeta.index + 1} de {chunkMeta.total}
            </p>
          )}
        </div>
      </header>

      <div className="ar-pdf-body">
        {isFirstChunk && (
          <div className="ar-pdf-parties">
            <div>
              <p className="ar-pdf-party-label">Escopo</p>
              <p className="ar-pdf-party-name">Vendas (type = sale)</p>
              <p className="ar-pdf-party-detail">Exclui pré-vendas do relatório</p>
            </div>
            <div>
              <p className="ar-pdf-party-label">Filtros aplicados</p>
              <p className="ar-pdf-party-detail">{buildFilterSummary(filters)}</p>
            </div>
          </div>
        )}

        <table className="ar-pdf-table">
          <thead>
            <tr>
              {visibleColumns.map((col) => (
                <th key={col.id} className={cellAlignClass(col.align)}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sales.map((row) => (
              <tr key={row.id}>
                {visibleColumns.map((col) => (
                  <td key={col.id} className={cellAlignClass(col.align)}>
                    {renderCell(col.id, row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {isLastChunk && (
          <>
            <div className="ar-pdf-summary">
              <div className="ar-pdf-total-box">
                <div className="ar-pdf-total-row">
                  <span>Vendas listadas</span>
                  <strong>{totals.count}</strong>
                </div>
                {totals.cancelledCount > 0 && (
                  <div className="ar-pdf-total-row">
                    <span>Canceladas ({totals.cancelledCount})</span>
                    <strong>{formatCurrency(totals.cancelledAmount)}</strong>
                  </div>
                )}
                <div className="ar-pdf-total-final">
                  <p className="ar-pdf-total-final-label">Total (exceto canceladas)</p>
                  <p className="ar-pdf-total-final-value">{formatCurrency(totals.amount)}</p>
                </div>
              </div>
            </div>

            <footer className="ar-pdf-footer">{COMPANY_FOOTER}</footer>
          </>
        )}
      </div>
    </div>
  )
})

export default VendasReportPdfCanvas
